"use client";

import { useMemo, useState } from "react";
import Cookies from "js-cookie";

import { Button, Form } from "@marketing-sites/ui/primitives";
import { useMarketoForm } from "@marketing-sites/marketo/use-marketo-form";
import { renderFields } from "@marketing-sites/marketo/render-fields";
import { toFieldProps } from "@marketing-sites/marketo/field-props";
import { filterFormFieldsByLocation } from "@marketing-sites/utils/filter-form-fields-by-location";
import { DRLP_MARKETO_FORMS } from "@marketing-sites/drlp";

import { useUserLocation } from "@/context/user-location.context";
import { getFormFields } from "@/lib/marketo/get-form-fields";
import { FORM_CONFIGS } from "@/constants/marketo/form-ids";

import { submitDrlpContentRegistration } from "../../_actions/submit-content-registration.action";
import { submitDrlpViewDemosGate } from "../../_actions/submit-view-demos.action";
import { rememberSubmittedLeadValues } from "@/lib/marketo/remember-lead-values";
import { HeroFormSuccess } from "./success-message";

/**
 * Resolves which Marketo form config + submit action the fallback form uses.
 * Defaults to Content Registration (1021) for every value other than the View
 * Demos gate (2377), preserving existing behavior for unset/legacy docs.
 */
export function resolveFallbackFormConfig(marketoFormId?: string) {
  if (marketoFormId === DRLP_MARKETO_FORMS.VIEW_DEMOS_GATE) {
    return {
      formConfig: FORM_CONFIGS.VIEW_DEMOS_GATE,
      action: submitDrlpViewDemosGate,
      enforceBusinessEmail: true,
      gtmFormType: "view-demos-gate",
    };
  }
  return {
    formConfig: FORM_CONFIGS.CONTENT_REGISTRATION,
    action: submitDrlpContentRegistration,
    enforceBusinessEmail: false,
    gtmFormType: "progressive-fallback",
  };
}

interface FallbackFormProps {
  /** Marketo form id selected in Sanity. Defaults to Content Registration
   *  (1021) for any value other than the View Demos gate (2377). */
  marketoFormId?: string;
  /** Explicit post-submit behavior from Sanity. When unset, inferred from the
   *  presence of a redirect URL / PDF (legacy docs). */
  formPostSubmitAction?: "showMessage" | "redirect" | "downloadPdf";
  ctaText?: string;
  successTitleText?: string;
  successBodyText?: string;
  successRedirectUrl?: string;
  pdfUrl?: string;
  pdfFilename?: string;
}

function downloadPdf(pdfUrl: string, pdfFilename?: string) {
  fetch(pdfUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pdfFilename || "download.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((err) => {
      console.error("Failed to download PDF:", err);
      window.location.href = pdfUrl;
    });
}

/**
 * Content Registration (1021) or View Demos (2377) form rendered in the DRLP
 * hero, resolved from `marketoFormId` via `resolveFallbackFormConfig`. Also
 * used as the fallback when the native progressive profiling form (2601)
 * fails to load. Built on intercom-com's standard Marketo stack
 * (`renderFields` + `useMarketoForm`), which fires GTM, lead-recovery and
 * Intercom-enrichment effects automatically on success.
 */
export function FallbackForm({
  marketoFormId,
  formPostSubmitAction,
  ctaText,
  successTitleText = "Thank you!",
  successBodyText = "We will be in touch shortly.",
  successRedirectUrl,
  pdfUrl,
  pdfFilename,
}: FallbackFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isOptInCountry } = useUserLocation();
  const cookies = Cookies.get();

  const { formConfig, action, enforceBusinessEmail, gtmFormType } =
    resolveFallbackFormConfig(marketoFormId);
  const { id, fallbackFields } = formConfig;

  // Honor the explicit Sanity action; only fall back to inferring from the
  // presence of a redirect URL / PDF for legacy docs that predate the field.
  // Hidden Sanity values must not silently trigger the wrong behavior.
  const effectiveAction =
    formPostSubmitAction ||
    (successRedirectUrl ? "redirect" : pdfUrl ? "downloadPdf" : "showMessage");

  const fields = useMemo(
    () =>
      toFieldProps(
        filterFormFieldsByLocation(
          getFormFields(id, fallbackFields),
          isOptInCountry,
        ),
        {
          enforceBusinessEmail,
          cookies,
          personalEmailMetadata: {
            analyticsId: "form_drlp_hero_personal_email_detected",
          },
        },
      ),
    [id, fallbackFields, enforceBusinessEmail, isOptInCountry, cookies],
  );

  const { status, formAction, pending, handleFormSubmit } = useMarketoForm({
    action,
    formId: id,
    analyticsLocation: "DRLP Hero Form",
    submitMetadata: {
      analyticsId:
        marketoFormId === DRLP_MARKETO_FORMS.VIEW_DEMOS_GATE
          ? "form_drlp_hero_view_demos_gate_submit"
          : "form_drlp_hero_content_registration_submit",
    },
    gtmEventMetadata: { formType: gtmFormType },
    onSuccess: (result) => {
      rememberSubmittedLeadValues(result.formValues);
      if (effectiveAction === "redirect" && successRedirectUrl) {
        window.location.href = successRedirectUrl;
        return;
      }
      if (effectiveAction === "downloadPdf" && pdfUrl) {
        downloadPdf(pdfUrl, pdfFilename);
      }
      setIsSubmitted(true);
    },
  });

  if (isSubmitted) {
    return (
      <HeroFormSuccess
        title={successTitleText}
        body={successBodyText}
        pdfUrl={effectiveAction === "downloadPdf" ? pdfUrl : undefined}
      />
    );
  }

  return (
    <Form.Root
      action={formAction}
      onSubmit={handleFormSubmit}
      data-analytics-region="Form: DRLP Hero"
      data-analytics-id="form_drlp_hero_fallback"
      className="flex w-full max-w-[520px] flex-col gap-4"
    >
      {renderFields(fields)}

      <Button
        analyticsId="btn_drlp_hero_form_fallback_submit"
        type="submit"
        disabled={pending}
        className="mt-2 cursor-pointer"
      >
        {pending ? "Please wait..." : ctaText || "Submit"}
      </Button>

      {status?.error && (
        <p className="text-body text-error border-error block w-full border p-1.5 font-sans">
          {status.error}
        </p>
      )}
    </Form.Root>
  );
}
